import { useInfiniteQuery, useQuery, useQueryClient } from "react-query";
import {
  getCreditLog,
  getPricingTypes,
  getProduct,
  getProductList,
  getProductUnits,
  getServiceDetails,
  getCompositeProduct,
  getProductTypes,
  getCategoriesLinkedToProduct,
  getStockTransfersList,
  getStockTransferItems,
  getStockTransferDetails,
  getStoreDetails,
  getTrackItems,
  getShippingsList,
  getShipping,
  getMenus,
  storeFrontShippingGroup,
  getProductsBatch,
  getProductBatchDetails,
  getStoreWallets,
  getStoreWalletDetails,
  getStoreWalletTransaction,
  getStoreWalletTransactionDetails,
  getAuthorizationUsers,
  getOrderActionDetails,
  getCustomerStoreWallets,
  getReturn,
  getOrderTables,
  getReconciliations,
  getProductBatchesCustomised,
  getReturnsList,
  getReturnRequestsList,
  getReturnRequestDetails,
  getRestockDetails,
  getScheduleDelivery,
  compositeProductBatchesCostPrice,
  getServiceMonthlyAvailablity,
  getServiceDateAvailablity,
  getCompositeProductList,
  getReconciliationDetails,
  getReconciliationStockHistory,
  getStockRemovalRequestsList,
  getStockRemovalRequestDetails,
} from "../../../services/inventory";
import { getSuppliers } from "../../../services/suppliers";
import { useDispatch } from "react-redux";
import { getCategoryList, getStoreList } from "../../../services/inventory";
import { setStoreList } from "../../../store/modules/profile";
import { getTags } from "../../../services/tags";
import {
  getCreditLogs,
  getCreditLogTransaction,
} from "../../../services/credit";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import { cleanObject } from "../../../helpers";

export const useGetStockTypes = () => {
  return useQuery(["getStockTypes"], () =>
    getProductTypes().then((res) => res?.data?.detail),
  );
};

export const useGetStockUnits = () => {
  return useQuery(
    ["getStockUnits"],
    () => getProductUnits().then((res) => res?.data?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetCompositeProduct = ({ id, isEnabled = true }) => {
  return useQuery(
    ["getCompositeProduct", id],
    () => getCompositeProduct(id).then((res) => res?.data),
    { enabled: isEnabled, staleTime: Infinity },
  );
};

export const useGetProductDetails = ({ id, isEnabled = true }) => {
  return useQuery(
    ["getProductDetails", `${id}`],
    () => getProduct(id).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: !!id && isEnabled ? true : false,
    },
  );
};

export const useGetServiceDetails = (id, isEnabled = true) => {
  return useQuery(
    ["getServiceDetails", `${id}`],
    () => getServiceDetails(id).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled ? true : false,
    },
  );
};

export const useGetPricingTypes = (isEnabled = true) => {
  return useQuery(
    ["getPricingTypes"],
    () => getPricingTypes().then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetStoreList = ({
  isEnabled = true,
  allowDispatch = true,
  queryParams = {},
}) => {
  const dispatch = useDispatch();

  return useQuery(
    ["getStoreList", queryParams],
    () =>
      getStoreList(queryParams).then((res) => {
        if (allowDispatch) dispatch(setStoreList(res?.data?.data));
        return res?.data?.data;
      }),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetStoreDetails = (storeId) => {
  return useQuery(
    ["getStoreDetails", storeId],
    () => getStoreDetails(storeId).then((res) => res?.data?.data),
    {
      enabled: storeId !== null,
      staleTime: Infinity,
    },
  );
};

export const useGetAllProductsList = ({
  queries = "",
  queryParams = {},
  onSuccess = () => {},
  enabled = true,
}) => {
  return useQuery(
    ["getProductList", queries, queryParams],
    () => getProductList(queries, queryParams).then((res) => res?.data),
    {
      enabled: queries !== "" || enabled,
      staleTime: Infinity,
      onSuccess: (res) => onSuccess(res?.data),
    },
  );
};

export const useGetAllCompositeProducts = ({
  queries = "",
  queryParams = {},
  onSuccess = () => {},
  enabled = true,
}) => {
  return useQuery(
    ["getCompositeProductList", queries, queryParams],
    () =>
      getCompositeProductList(queries, queryParams).then((res) => res?.data),
    {
      enabled: queries !== "" || enabled,
      staleTime: Infinity,
      onSuccess: (res) => onSuccess(res?.data),
    },
  );
};

// getReturnsList
export const useGetReturnsList = ({ queries = "", enabled = true }) => {
  return useQuery(
    ["getReturnsList", queries],
    () => getReturnsList(queries).then((res) => res?.data),
    {
      enabled: enabled,
      staleTime: Infinity,
    },
  );
};

export const useGetReturnRequestsList = ({ queries = "", enabled = true }) => {
  return useQuery(
    ["getReturnRequestsList", queries],
    () => getReturnRequestsList(queries).then((res) => res?.data),
    {
      enabled: enabled,
      staleTime: Infinity,
    },
  );
};

export const useGetReturnRequestDetails = ({ id, isEnabled = true }) => {
  return useQuery(
    ["getReturnRequestDetails", `${id}`],
    () => getReturnRequestDetails(id).then((res) => res?.data?.data),
    {
      enabled: Boolean(isEnabled && id),
      staleTime: Infinity,
    },
  );
};

export const useGetStockRemovalRequestsList = ({
  queries = "",
  queryParams = {},
  enabled = true,
}) => {
  return useQuery(
    ["getStockRemovalRequestsList", queries, queryParams],
    () =>
      getStockRemovalRequestsList(queries, queryParams).then(
        (res) => res?.data,
      ),
    {
      enabled: enabled,
      staleTime: Infinity,
    },
  );
};

export const useGetStockRemovalRequestDetails = ({ id, isEnabled = true }) => {
  return useQuery(
    ["getStockRemovalRequestDetails", `${id}`],
    () => getStockRemovalRequestDetails(id).then((res) => res?.data?.data),
    {
      enabled: Boolean(isEnabled && id),
      staleTime: Infinity,
    },
  );
};

export const useReturnsDetails = (id, isEnabled = true) => {
  return useQuery(
    ["getReturn", id],
    () => getReturn(id).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled ? true : false,
    },
  );
};

export const useGetTagList = (isEnabled = true) => {
  return useQuery(
    ["getTagList"],
    () => getTags().then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetSuppliers = (queries = "", returnPagination = false) => {
  return useQuery(
    ["getSuppliers", queries, returnPagination],
    () =>
      getSuppliers(queries).then((res) => {
        if (returnPagination) return res?.data;
        return res?.data?.data;
      }),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetSuppliersForSelectField = ({ queryParams = {} }) => {
  const queryClient = useQueryClient();

  const loadOptions = async (inputValue) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["getSuppliers", { query: inputValue, ...queryParams }],
        queryFn: () =>
          getSuppliers("", {
            query: inputValue ? inputValue : "",
            ...queryParams,
          }).then((res) => res.data),
        staleTime: Infinity,
      });

      console.log(data?.data);
      return (data?.data || [])?.map((item) => ({
        value: item?.id,
        label: `${item?.name}`,
      }));
    } catch (error) {
      return [];
    }
  };

  return {
    loadOptions,
  };
};

export const useGetProductCategoryList = (isEnabled = true) => {
  return useQuery(
    ["getProductCategoryList"],
    () => getCategoryList().then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetCategoriesLinkedToProduct = ({
  isEnabled = true,
  queries = "",
}) => {
  return useQuery(
    ["categoriesLinkedToProduct", queries],
    () => getCategoriesLinkedToProduct(queries).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetCreditLog = (queries = "") => {
  return useQuery(
    ["getCreditLog", queries],
    () => getCreditLog(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetCreditLogDetails = (queries = "") => {
  return useQuery(
    ["getCreditLogDetails", queries],
    () => getCreditLogs(queries).then((res) => res?.data),
    {
      enabled: queries !== "",
      staleTime: Infinity,
    },
  );
};

export const useGetStockTransfersList = ({ queries = "", enabled = true }) => {
  return useQuery(
    ["getStockTransfersList", queries],
    () => getStockTransfersList(queries).then((res) => res?.data),
    {
      enabled: enabled,
    },
  );
};

export const useGetStockTransferDetails = ({ id }) => {
  return useQuery(
    ["getStockTransferDetails", `${id}`],
    () => getStockTransferDetails(id).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetStockTransferItems = ({ id }) => {
  const { ref: autoScrollRef, inView } = useInView();
  const stockTransferItemsQueries = useInfiniteQuery(
    ["getStockTransferItems", id],
    ({ pageParam }) =>
      getStockTransferItems(
        cleanObject({
          id: id,
          page: pageParam,
        }),
      ).then((res) => {
        const formattedData = res?.data?.data?.map((item) => ({
          stock_transfer_item_id: item?.id,
          name: item?.product_name,
          stock_quantity: item.product_property_from?.stock_quantity,
          quantity: item?.quantity_to_transfer,
          variant: item?.variant_name,
          unit: item?.unit,
          update_destination_pricing: item?.update_destination_pricing,
          reference_number: item?.reference_number,
        }));

        return {
          data: formattedData,
          meta: res?.data?.meta,
        };
      }),
    {
      staleTime: Infinity,
      getNextPageParam: ({ meta: { next_page } }) =>
        next_page === null ? undefined : next_page,
    },
  );
  const { data, hasNextPage, fetchNextPage } = stockTransferItemsQueries;
  const stockTransferItems = new Map();
  // eslint-disable-next-line array-callback-return
  data?.pages?.map((page) => {
    stockTransferItems.set(`${page.meta.current_page}`, page?.data);
  });
  const stockTransferItemList = [].concat(...stockTransferItems.values());

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, fetchNextPage, hasNextPage]);

  return {
    ...stockTransferItemsQueries,
    autoScrollRef,
    data: stockTransferItemList,
  };
};

export const useGetTrackItems = ({
  queries = "",
  otherParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getTrackItems", queries, otherParams],
    () => getTrackItems(queries, otherParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetShippingsList = ({ queries }) => {
  return useQuery(
    ["getShippingsList", queries],
    () => getShippingsList(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetShippingDetails = (shippingId) => {
  return useQuery(
    ["getShippingDetails", shippingId],
    () => getShipping(shippingId).then((res) => res?.data?.data),
    {
      enabled: !!shippingId,
    },
  );
};

export const useGetMenus = ({ queries = "", isEnabled = true }) => {
  return useQuery(
    ["getMenus", queries],
    () => getMenus(queries).then((res) => res?.data?.data),
    {
      enabled: isEnabled,
    },
  );
};

export const useGetStoreFrontShippingGroup = (menuId) => {
  return useQuery(
    ["getstoreFrontShippingGroup", menuId],
    () => storeFrontShippingGroup(menuId).then((res) => res?.data?.data),
    {
      enabled: !!menuId,
      staleTime: Infinity,
    },
  );
};

export const useGetProductsBatch = (queries) => {
  return useQuery(
    ["getProductsBatch", queries],
    () => getProductsBatch(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetStoreWallets = ({ queries = "" }) => {
  return useQuery(
    ["getStoreWallets", queries],
    () => getStoreWallets(queries).then((res) => res?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetProductBatchDetails = (id) => {
  return useQuery(
    ["getProductBatchDetails", id],
    () => getProductBatchDetails(id).then((res) => res?.data?.data),
    {
      enabled: id !== null,
    },
  );
};

export const useGetStoreWalletDetails = (walletId) => {
  return useQuery(
    ["getStoreWalletDetails", walletId],
    () => getStoreWalletDetails(walletId).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetStoreWalletTransaction = ({
  queries = "",
  queryParams = {},
  isEnabled = true,
}) => {
  return useQuery(
    ["getStoreWalletTransaction", queries, queryParams],
    () =>
      getStoreWalletTransaction(queries, queryParams).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};

export const useGetStoreWalletTransactionDetails = (tnxId) => {
  return useQuery(
    ["getStoreWalletTransactionDetails", tnxId],
    () =>
      getStoreWalletTransactionDetails(tnxId).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
    },
  );
};

export const useGetAuthorizationUsers = (action) => {
  return useQuery(
    ["getAuthorizationUsers", action],
    () => getAuthorizationUsers(action).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: !!action,
    },
  );
};

export const useGetOrderActionDetails = ({
  store_id,
  action,
  onError,
  onSuccess,
  enabled = false,
  params = {},
}) => {
  return useQuery(
    ["getOrderActionDetails", store_id, action],
    () =>
      getOrderActionDetails({ store_id, action, params }).then(
        (res) => res?.data?.data,
      ),
    {
      staleTime: Infinity,
      enabled: enabled && action ? true : false,
      onError,
      onSuccess,
      retry: 0,
      cacheTime: 0,
    },
  );
};
export const useGetCreditLogTransaction = (id = "") => {
  return useQuery(
    ["getCreditLogTransaction", id],
    () => getCreditLogTransaction(id).then((res) => res?.data?.data),
    {
      enabled: id !== "",
      staleTime: Infinity,
    },
  );
};

export const useGetCustomerStoreWallets = ({ customer_id }) => {
  return useQuery(
    ["getCustomerStoreWallets", customer_id],
    () => getCustomerStoreWallets(customer_id).then((res) => res?.data),
    {
      enabled: !!customer_id,
      staleTime: Infinity,
    },
  );
};

export const useGetTables = ({ queryParams = {}, params = "" }) => {
  return useQuery(["getOrderTables", params, queryParams], () =>
    getOrderTables(params, queryParams).then((res) => res.data),
  );
};

export const useGetReconciliations = ({ queries = "" }) => {
  return useQuery(
    ["getReconciliations", queries],
    () => getReconciliations(queries).then((res) => res.data),
    { staleTime: Infinity },
  );
};

export const useGetProductBatchesCustomised = ({
  queries = "",
  querParams = {},
}) => {
  return useQuery(
    ["getProductBatchesCustomised", queries, querParams],
    () =>
      getProductBatchesCustomised(queries, querParams).then(
        (res) => res.data?.data,
      ),
    { staleTime: Infinity },
  );
};

export const useGetRestockDetails = ({ id, isEnabled = true }) => {
  return useQuery(
    ["getRestockDetails", id],
    () => getRestockDetails(id).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: !!id && isEnabled ? true : false,
    },
  );
};

export const useGetScheduleDelivery = ({ storefrontId, isEnabled = true }) => {
  return useQuery(
    ["getScheduleDelivery", storefrontId],
    () => getScheduleDelivery(storefrontId).then((res) => res?.data?.data),
    {
      enabled: !!storefrontId && isEnabled ? true : false,
      staleTime: Infinity,
    },
  );
};

export const useGetOrderBatchesCostPrice = ({ data, isEnabled = true }) => {
  return useQuery(
    ["getOrderBatchesCostPrice", data],
    () => compositeProductBatchesCostPrice(data).then((res) => res?.data?.data),
    {
      enabled: isEnabled,
      staleTime: Infinity,
    },
  );
};

export const useGetServiceMonthlyAvailablity = ({
  queryParams = {},
  service_id,
}) => {
  return useQuery(
    ["getServiceMonthlyAvailablity", queryParams, `${service_id}`],
    () =>
      getServiceMonthlyAvailablity({ queryParams, service_id }).then(
        (res) => res?.data?.data,
      ),
    {
      staleTime: Infinity,
      enabled: queryParams?.month && queryParams?.year ? true : false,
    },
  );
};

export const useGetServiceDateAvailablity = ({
  queryParams = {},
  service_id,
}) => {
  return useQuery(
    ["getServiceDateAvailablity", queryParams, `${service_id}`],
    () =>
      getServiceDateAvailablity({ queryParams, service_id }).then(
        (res) => res?.data?.data,
      ),
    {
      staleTime: Infinity,
      enabled: queryParams?.date ? true : false,
    },
  );
};

export const useGetReconciliationDetails = ({
  id,
  isEnabled = true,
  onSuccess = () => {},
}) => {
  return useQuery(
    ["getReconciliationDetails", `${id}`],
    () => getReconciliationDetails(id).then((res) => res?.data?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
      onSuccess: (res) => {
        onSuccess(res);
      },
    },
  );
};

export const useGetReconciliationStockHistory = ({ id, isEnabled = true }) => {
  return useQuery(
    ["getReconciliationStockHistory", `${id}`],
    () => getReconciliationStockHistory(id).then((res) => res?.data),
    {
      staleTime: Infinity,
      enabled: isEnabled,
    },
  );
};
